import { FC } from 'react';
import { cn } from 'shared/lib';
import ColorSelect from 'shared/ui/ColorSelect/ColorSelect';
import { BsFillPlusCircleFill } from 'react-icons/bs';
import { TTheme } from 'widgets/choose-background/ui/types';
import { colorList, gradientList } from 'widgets/choose-background/model';
import { FiInfo } from 'react-icons/fi';

interface IProps {
  selectedBackground: string;
  theme: TTheme;
  customColor: string | null;
  onChange: (value: string | null) => void;
  setCustomColor: (color: string | null) => void;
}

export const ColorsTab: FC<IProps> = ({
  selectedBackground,
  theme = 'dark',
  customColor,
  onChange,
  setCustomColor
}) => {
  return (
    <>
      <div className="ag-text-xl ag-font-semibold ag-mb-4 ag-flex ag-flex-col ag-items-start ag-justify-start ag-gap-2">
        Solid colours{' '}
        {selectedBackground === '' && (
          <div className="ag-group ag-relative ag-w-full">
            <div className="ag-border-red-300 ag-border ag-rounded-xl ag-flex ag-items-center ag-p-3 ag-bg-red-50 ag-gap-3">
              <div className="ag-rounded-full ag-bg-red-100 ag-text-lg ag-text-red-300 ag-flex ag-items-center ag-justify-center ag-p-2">
                <FiInfo />
              </div>
              <div className="ag-text-red-700 ag-text-sm ag-font-medium">
                Custom backgrounds are in beta and currently work only with
                greenscreen. For best results, create your avatar with a custom
                background or use a pre-made one.
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="ag-flex ag-flex-wrap ag-gap-2 ag-mb-6">
        <div className="ag-grid ag-grid-cols-3 ag-w-full ag-gap-2">
          <ColorSelect
            className={cn(
              'ag-w-full ag-h-custom-72 ag-rounded-lg ag-border',
              selectedBackground === customColor && customColor !== ''
                ? 'ag-border-[3px] ag-border-primary-600'
                : theme === 'dark'
                ? 'ag-border-neutral-700'
                : 'ag-border-neutral-200'
            )}
            onChange={(color) => {
              onChange(color as string);
              setCustomColor(color as string);
            }}
            pickerPosition="right"
            disableTransparent
            color={customColor as string}
          >
            <BsFillPlusCircleFill color="black" size={32} />
          </ColorSelect>
          <div
            className={cn(
              'ag-w-full ag-overflow-hidden lg:ag-overflow-visible ag-h-custom-72 ag-group ag-rounded-lg ag-cursor-pointer ag-border ag-relative',
              selectedBackground === ''
                ? 'ag-border-[3px] ag-border-primary-600'
                : theme === 'dark'
                ? 'ag-border-neutral-700'
                : 'ag-border-neutral-200'
            )}
            onClick={() => {
              onChange('');
            }}
            color={customColor as string}
          >
            <div className="ag-absolute ag-w-full ag-h-[2px] ag-bg-red-600 ag-rotate-[20deg] sm:ag-rotate-[14deg] lg:ag-rotate-[20deg] ag-top-1/2 ag-left-0 transform -translate-y-1/2"></div>
          </div>

          {colorList.map((color) => (
            <div
              className={cn(
                'ag-w-full ag-h-custom-72 ag-rounded-lg ag-border ag-cursor-pointer',
                selectedBackground === color
                  ? 'ag-border-[3px] ag-border-primary-600'
                  : theme === 'dark'
                  ? 'ag-border-neutral-700'
                  : 'ag-border-neutral-200'
              )}
              onClick={() => onChange(color)}
              style={{ background: color }}
              key={color}
            />
          ))}
        </div>
      </div>
      <div className="ag-text-sm ag-font-semibold ag-mb-4">Gradients</div>
      <div className="ag-flex ag-flex-wrap ag-gap-2">
        {gradientList.map((color) => (
          <div
            className={cn(
              'ag-w-32 ag-h-custom-72 ag-rounded-lg ag-border ag-cursor-pointer',
              selectedBackground === color
                ? 'ag-border-[3px] ag-border-primary-600'
                : theme === 'dark'
                ? 'ag-border-neutral-700'
                : 'ag-border-neutral-200'
            )}
            onClick={() => onChange(color)}
            style={{ background: color }}
            key={color}
          />
        ))}
      </div>
    </>
  );
};
